import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Alert, App, Button as AntButton, Card, Checkbox, Collapse, InputNumber, Input, Modal, Segmented, Select, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { Pencil, FileText, Undo2, TriangleAlert, MessageCircleQuestion, Trash2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import {
  HTS_ANSWER_CLASSIFICATION_LABELS,
  HTS_CLASSIFICATION_TOPIC_BY_ID,
  HTS_PERFECT_PERSON_PROBE_ID,
  HTS_PRIORITY_PILLAR_SCORE_THRESHOLD,
  HTS_READINESS_BAND_THRESHOLDS,
  HTS_READINESS_PILLAR_CONFIG_BY_KEY,
  HTS_STRONG_PILLAR_COUNT,
  HtsAnswerClassification,
  HtsAnswerOverrideHistoryKind,
  HtsConsistencyFlagOverrideDecision,
  HtsConsistencyFlagOverrideHistoryKind,
  HtsFlagPriority,
  HtsInputType,
  HtsReadinessBand,
  HtsReadinessDimensionOverrideHistoryKind,
  HtsReadinessNarrativeSource,
  HtsReviewNoteTarget,
  MAX_ANSWER_MESSAGE_LENGTH,
  computeCompositeScore,
  computePillarWeightPercents,
  computePillarScore,
  getHtsReadinessBand,
  isHtsReviewPending,
  isSuitabilityTopic,
  resolveEffectiveAnswerFlag,
  resolveEffectiveDimensionWeights,
} from '@evaltick/shared';
import type { HtsAnswerFlagColumns, HtsAnswerValue, HtsGroupField } from '@evaltick/shared';
import { AdminService } from '@/services/adminService';
import type {
  AssessmentResultResponse,
  AdminHtsAnswerDetail,
  AdminHtsConsistencyFlagDetail,
  AdminHtsReadinessDimensionScore,
  AdminHtsReadinessNarrative,
  AdminHtsReadinessNarrativeHistoryEntry,
  AdminHtsReadinessNarrativePreview,
  AdminHtsSubmissionDetail,
  AdminHtsReadinessPillarScore,
  AdminHtsReviewBatch,
} from '@/services/adminService';
import { cn, scoreToTagColor } from '@/utils';
import { formatDate } from '@/utils/formatters';
import { Button } from '@/components/Button';
import styles from './AdminSessionResults.module.scss';

/** Shown when htsAnswerChecks fail-closed with `classification: null` (no verdict produced). */
const UNCLASSIFIED_LABEL = 'Unclassified';

/** Shared question/answer styling — italic question, default-style answer — reused across probe/flagged/sensitive rows. */
const QAQuestion: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography.Text italic type="secondary" className={styles.qaQuestion}>
    {children}
  </Typography.Text>
);

const QAAnswer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography.Paragraph className={styles.qaAnswer}>{children}</Typography.Paragraph>
);

/** Shared by ResponseCard (competency dimensions) and ReadinessPillarCard (HTS pillar dimensions) below. */
export const DimensionScores: React.FC<{ scores: AssessmentResultResponse['dimensionScores'] }> = ({ scores }) => {
  if (!scores || scores.length === 0) return null;
  return (
    <div className={styles.dimensions}>
      {scores.map((d) => (
        <div key={d.key} className={styles.dimension}>
          <div className={styles.dimensionHeader}>
            <Typography.Text strong>{d.key}</Typography.Text>
            <span className={styles.dimensionMeta}>
              {d.weight !== null && (
                <Typography.Text type="secondary" className={styles.dimensionWeight}>
                  {Math.round(d.weight * 100)}% weight
                </Typography.Text>
              )}
              <Tag>{d.score != null ? `${d.score} / 100` : 'Not assessed'}</Tag>
            </span>
          </div>
          <Typography.Text type="secondary">{d.rationale}</Typography.Text>
        </div>
      ))}
    </div>
  );
};

/** Maps the raw band enum to the rubric's display label; exported so the results list matches. */
export const bandLabelFor = (band: HtsReadinessBand): string =>
  HTS_READINESS_BAND_THRESHOLDS.find((t) => t.band === band)?.label ?? band;

/** Same as bandLabelFor for pillar keys; falls back to the raw key so a retired key still reads back on old profiles. */
const pillarLabelFor = (key: string): string => HTS_READINESS_PILLAR_CONFIG_BY_KEY[key]?.label ?? key;

/** Renders HtsAnswerValue as prose; an empty REPEATABLE_GROUP arrives as the literal string "[]", not an array, so both are handled. */
const answerText = (value: HtsAnswerValue): string => {
  if (Array.isArray(value)) return value.length === 0 ? 'None' : value.join(', ');
  return value === '[]' ? 'None' : value;
};

/** One REPEATABLE_GROUP row — sub-field key to its (always scalar) value, matching HtsQuestionCard's compiled shape. */
type GroupRow = Record<string, string>;

/** Parsing failures fall back to no rows, same discipline as HtsQuestionCard's parseGroupRows. */
const parseGroupRows = (value: HtsAnswerValue): GroupRow[] => {
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** A CHECKBOXES sub-field's selected options are their own JSON-stringified array within the row's scalar value — same double-encoding parseCheckboxRowValue unwraps. */
const formatGroupFieldValue = (field: HtsGroupField, rawValue: string | undefined): string => {
  if (!rawValue) return '—';
  if (field.inputType !== HtsInputType.CHECKBOXES) return rawValue;
  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed.join(', ') : '—';
  } catch {
    return rawValue;
  }
};

/** Renders a REPEATABLE_GROUP answer as a table (one column per field) instead of raw JSON — scales better than stacked blocks for long histories. */
const RepeatableGroupAnswer: React.FC<{ value: HtsAnswerValue; groupFields: HtsGroupField[] }> = ({ value, groupFields }) => {
  const rows = parseGroupRows(value);
  if (rows.length === 0) return <QAAnswer>None</QAAnswer>;
  const columns = groupFields.map((field) => ({
    title: field.label,
    dataIndex: field.key,
    key: field.key,
    render: (rawValue: string | undefined) => formatGroupFieldValue(field, rawValue),
  }));
  return (
    <Table
      className={styles.groupAnswerTable}
      size="small"
      pagination={false}
      columns={columns}
      dataSource={rows}
      rowKey={(_, index) => index ?? 0}
      scroll={{ x: 'max-content' }}
      styles={{ content: { overflowX: 'scroll' } }}
    />
  );
};

/** Shared answer-value renderer — plain prose via answerText, or RepeatableGroupAnswer's rows when the question has groupFields. */
const AnswerValue: React.FC<{ answer: AdminHtsAnswerDetail }> = ({ answer }) =>
  answer.groupFields && answer.groupFields.length > 0 ? (
    <RepeatableGroupAnswer value={answer.value} groupFields={answer.groupFields} />
  ) : (
    <QAAnswer>{answerText(answer.value)}</QAAnswer>
  );

// =============================================================================
// Staging — unsaved edits for this browser session, one entry per touched row.
// `valueTouched` decides whether an entry becomes a value override or a plain
// note at Save/Release.
// =============================================================================

/** Which control an edit came from — HardGate shares Answer's note target but has its own override array, kept apart until buildReviewBatch collapses them. */
const StagedTargetKind = {
  Answer: HtsReviewNoteTarget.Answer,
  HardGate: 'hardGate',
  ConsistencyFlag: HtsReviewNoteTarget.ConsistencyFlag,
  Dimension: HtsReviewNoteTarget.Dimension,
  Probe: HtsReviewNoteTarget.Probe,
} as const;
type StagedTargetKind = (typeof StagedTargetKind)[keyof typeof StagedTargetKind];

interface StagedEntry {
  kind: StagedTargetKind;
  /** True once the admin touched the value control itself (Select, number input, or one of the two decision buttons) — not just the note. */
  valueTouched: boolean;
  note: string;
  classification?: HtsAnswerClassification | null;
  flagged?: boolean | null;
  priority?: HtsFlagPriority | null;
  decision?: HtsConsistencyFlagOverrideDecision | null;
  score?: number | null;
}

/** Keyed by questionId, flagId, or `${pillarKey}:${dimensionKey}` — matching the batch shape's own `id` convention. */
type StagingState = Record<string, StagedEntry>;

const buildReviewBatch = (staging: StagingState, scoringFailureAcknowledged = false): AdminHtsReviewBatch => {
  const batch: AdminHtsReviewBatch = {
    answerOverrides: [],
    hardGateOverrides: [],
    consistencyFlagOverrides: [],
    dimensionOverrides: [],
    itemNotes: [],
    scoringFailureAcknowledged,
  };

  for (const [key, entry] of Object.entries(staging)) {
    const note = entry.note.trim();
    if (!entry.valueTouched) {
      if (!note) continue; // nothing real staged — an emptied note field
      const target = entry.kind === StagedTargetKind.HardGate ? HtsReviewNoteTarget.Answer : entry.kind;
      batch.itemNotes.push({ target, id: key, note });
      continue;
    }
    // Defensive only — every modal already blocks Save until a touched value has a note (see AdminHtsReviewBatch).
    if (!note) continue;
    switch (entry.kind) {
      case StagedTargetKind.Answer:
        if (entry.classification == null) continue;
        batch.answerOverrides.push({ questionId: key, classification: entry.classification, note });
        break;
      case StagedTargetKind.HardGate:
        if (entry.flagged == null) continue;
        batch.hardGateOverrides.push({
          questionId: key,
          flagged: entry.flagged,
          priority: entry.priority ?? null,
          note,
        });
        break;
      case StagedTargetKind.ConsistencyFlag:
        if (entry.decision == null) continue;
        batch.consistencyFlagOverrides.push({ flagId: key, decision: entry.decision, note });
        break;
      case StagedTargetKind.Dimension: {
        if (entry.score == null) continue;
        const [pillarKey, dimensionKey] = key.split(':');
        batch.dimensionOverrides.push({ pillarKey, dimensionKey, score: entry.score, note });
        break;
      }
    }
  }

  return batch;
};

// ── Effective-value resolution ──────────────────────────────────────────────
// Staged edit wins, else persisted override, else automated value. Reverting
// deletes the staged entry rather than staging a null — the `??` fallbacks
// below only satisfy StagedEntry's optional fields.
const effectiveClassificationFor = (
  answer: AdminHtsAnswerDetail,
  staged: StagedEntry | undefined
): HtsAnswerClassification | null =>
  staged?.valueTouched ? (staged.classification ?? answer.classification) : (answer.overrideClassification ?? answer.classification);

/** The staged edit folded into the answer's own override columns, so the shared resolver below reads it exactly as it will once saved. */
const withStagedOverrides = (answer: AdminHtsAnswerDetail, staged: StagedEntry | undefined): HtsAnswerFlagColumns => {
  if (!staged?.valueTouched) return answer;
  return {
    ...answer,
    overrideClassification:
      staged.kind === StagedTargetKind.Answer ? (staged.classification ?? null) : answer.overrideClassification,
    overrideFlagged: staged.kind === StagedTargetKind.HardGate ? (staged.flagged ?? null) : answer.overrideFlagged,
    overridePriority: staged.kind === StagedTargetKind.HardGate ? (staged.priority ?? null) : answer.overridePriority,
  };
};

/** The row's live flag state, via the same resolver the API scores on. Null (neither suitability nor hard-gate) reads as unflagged. */
const effectiveFlagFor = (answer: AdminHtsAnswerDetail, staged: StagedEntry | undefined) =>
  resolveEffectiveAnswerFlag(withStagedOverrides(answer, staged), {
    classificationTopic: answer.classificationTopic,
    hardGateRule: answer.isHardGate,
  }) ?? { effectivePriority: null, effectiveReviewFlagged: false };

const effectiveConsistencyDecisionFor = (
  flag: AdminHtsConsistencyFlagDetail,
  staged: StagedEntry | undefined
): HtsConsistencyFlagOverrideDecision | null =>
  staged?.valueTouched ? (staged.decision ?? null) : (flag.overrideDecision ?? null);

const effectiveDimensionScoreFor = (dim: AdminHtsReadinessDimensionScore, staged: StagedEntry | undefined): number | null =>
  staged?.valueTouched ? (staged.score ?? null) : (dim.overrideScore ?? null);

/** A dimension carrying its live-effective `score` alongside `baselineScore` (the automated value), which the edit modal shows as the history list's opening row. */
type LiveDimensionScore = AdminHtsReadinessDimensionScore & { baselineScore: number };
type LivePillarScore = Omit<AdminHtsReadinessPillarScore, 'dimensions'> & { dimensions: LiveDimensionScore[] };

/** Client-side mirror of computeEffectiveHtsReadinessProfile — recomputes scores via the same shared pure functions, so the UI updates with no round-trip. */
const computeLiveEffectiveProfile = (
  hts: AdminHtsSubmissionDetail,
  staging: StagingState
): { compositeScore: number; band: HtsReadinessBand; pillars: LivePillarScore[] } | null => {
  const persistedPillars = hts.readinessProfile?.detail?.pillars;
  if (!persistedPillars) return null;

  const pillars: LivePillarScore[] = persistedPillars.map((pillar) => {
    const scored: LiveDimensionScore[] = pillar.dimensions.map((dim) => {
      const staged = staging[`${pillar.key}:${dim.key}`];
      const score = effectiveDimensionScoreFor(dim, staged) ?? dim.score;
      return { ...dim, score, baselineScore: dim.score };
    });

    // Re-derived, not read off the stored weights, so each "% weight" shown is
    // the one that dimension's current score earns.
    const dimensions = resolveEffectiveDimensionWeights(pillar.key, scored);
    const pillarScore = computePillarScore(dimensions);
    return { ...pillar, dimensions, pillarScore };
  });

  const compositeScore = computeCompositeScore(pillars);
  const band = getHtsReadinessBand(compositeScore);
  return { compositeScore, band, pillars };
};

// Guidance only — classification no longer auto-moves these dimensions (see
// computeConductHistory/computeHonestyCompleteness); an admin edits the
// dimension directly if the score should change.
const CLASSIFICATION_SCORING_HINT =
  'How this classification feeds the Integrity pillar — Conduct History: Unsuitable → capped at 0. Marginally unsuitable → capped at 25. Unresolved after max follow-ups → 50. Otherwise 75–100. A marginally suitable answer left unresolved also caps Honesty & Completeness at 25.';

// Guidance only — dismissing doesn't re-score Honesty & Completeness, so the copy states past effect, not a promise.
const CONSISTENCY_FLAG_SCORING_HINT =
  'An unresolved flag capped Honesty & Completeness at 25 when this was scored. Dismissing it records your decision but won\'t re-score the dimension — edit that score directly if it should change.';

const classificationSelectOptions = Object.values(HtsAnswerClassification).map((c) => ({
  value: c,
  label: HTS_ANSWER_CLASSIFICATION_LABELS[c],
}));

interface OverrideNoteFieldProps {
  value: string;
  onChange: (note: string) => void;
  showRequiredHint: boolean;
}

/** The one shared note input every override control (and every note-only row) routes through. */
const OverrideNoteField: React.FC<OverrideNoteFieldProps> = ({ value, onChange, showRequiredHint }) => (
  <div className={styles.noteField}>
    <Input.TextArea
      placeholder="Add a note…"
      autoSize={{ minRows: 2, maxRows: 6 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {showRequiredHint && <Typography.Text className={styles.noteRequiredHint}>A note is required for this change.</Typography.Text>}
  </div>
);

/** One entry in a modal's history list — a domain-agnostic shape each modal's own history converts into, so there's one shared list UI. */
interface HistoryItem {
  /** What changed, e.g. "Score set to 82 / 100" or "Note added". */
  label: string;
  note?: string | null;
  overriddenAt: string | null;
  /** Null for the AI-generated "initial" entry — attributed to the system, not an admin. */
  overriddenByName: string | null;
}

/** Oldest-to-newest history rendered by every override modal; the API's overrideHistory already includes the persisted "initial" AI-generated entry. */
const HistoryList: React.FC<{ items: HistoryItem[] }> = ({ items }) => (
  <div className={styles.historyList}>
    {items.map((item, i) => (
      <div key={i} className={styles.historyItem}>
        <div className={styles.historyItemHeader}>
          <Typography.Text strong>{item.label}</Typography.Text>
          <Typography.Text type="secondary" className={styles.historyMeta}>
            {item.overriddenByName ?? 'System'}
            {item.overriddenAt && ` · ${new Date(item.overriddenAt).toLocaleString()}`}
          </Typography.Text>
        </div>
        {item.note && <Typography.Paragraph className={styles.historyNote}>{item.note}</Typography.Paragraph>}
      </div>
    ))}
  </div>
);

/** A revert-to-baseline icon button — replaces the old "Clear override" text button everywhere an override can be cleared. */
const RevertOverrideButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <AntButton size="small" icon={<Undo2 style={{ width: '1em', height: '1em' }} />} title="Clear override" aria-label="Clear override" onClick={onClick} />
);

/** Binary "does this flag/gate hold" choice, shared by AnswerRow and ConsistencyFlagCard — a toggle since exactly one of the two is always true. */
const FlagStandsToggle: React.FC<{ value: boolean; onChange: (stands: boolean) => void }> = ({ value, onChange }) => (
  <Segmented
    size="small"
    value={value ? 'stands' : 'dismissed'}
    onChange={(v) => onChange(v === 'stands')}
    options={[
      { label: 'Confirmed', value: 'stands' },
      { label: 'Dismissed', value: 'dismissed' },
    ]}
  />
);

interface DimensionEditModalProps {
  pillarLabel: string;
  dimension: LiveDimensionScore;
  /** When the profile was scored — dates the automated score shown as the first history row. */
  computedAt: string;
  /** Whether this dimension already has a staged score override — keeps the note required even in a later session that doesn't re-touch the input. */
  hasPendingScoreOverride: boolean;
  draftNote: string;
  onSave: (score: number | null, scoreTouched: boolean, note: string) => void;
  onClose: () => void;
}

/** Opened by the dimension row's Edit button — the score input and note, together with the history the override is judged against. */
const DimensionEditModal: React.FC<DimensionEditModalProps> = ({
  pillarLabel,
  dimension,
  computedAt,
  hasPendingScoreOverride,
  draftNote,
  onSave,
  onClose,
}) => {
  const [score, setScore] = useState<number | null>(dimension.score);
  const [scoreTouched, setScoreTouched] = useState(false);
  const [note, setNote] = useState(draftNote);

  // Uses hasPendingScoreOverride, not scoreTouched alone — reopening the modal
  // resets scoreTouched, which would otherwise let an emptied note strand a
  // staged override with no note (rejected at Release).
  // Scores have no null/clear override, so a blank input must block Save —
  // checked first so it surfaces its own hint instead of also demanding a note.
  const scoreMissing = scoreTouched && score === null;
  const noteRequired = !scoreMissing && (scoreTouched || hasPendingScoreOverride) && !note.trim();

  // The automated score lives in baselineScore, not as a history entry — shown as the list's first row for context.
  const historyItems: HistoryItem[] = [
    {
      label: `Initial score: ${dimension.baselineScore} / 100`,
      note: dimension.rationale,
      overriddenAt: computedAt,
      overriddenByName: null,
    },
    ...dimension.overrideHistory.map((entry) => ({
      label:
        entry.kind === HtsReadinessDimensionOverrideHistoryKind.Score
          ? entry.score === null
            ? 'Score cleared (reverted to automatic)'
            : `Score set to ${entry.score} / 100`
          : 'Note added',
      note: entry.note,
      overriddenAt: entry.overriddenAt,
      overriddenByName: entry.overriddenByName,
    })),
  ];

  return (
    <Modal
      open
      title={`Edit score — ${pillarLabel}: ${dimension.label}`}
      onCancel={onClose}
      footer={
        <div className={styles.modalFooterActions}>
          <Button appearance="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            appearance="solid"
            disabled={noteRequired || scoreMissing}
            onClick={() => onSave(score, scoreTouched, note)}
          >
            Save
          </Button>
        </div>
      }
    >
      <Typography.Paragraph type="secondary">
        Override this dimension's score if the automated rating needs correcting, or leave the score as-is and just add a
        note. A note is required whenever the score is changed.
      </Typography.Paragraph>
      {historyItems.length > 0 && (
        <>
          <Typography.Title level={5} className={styles.modalSectionTitle}>
            History
          </Typography.Title>
          <HistoryList items={historyItems} />
        </>
      )}
      <InputNumber
        min={0}
        max={100}
        value={score}
        onChange={(value) => {
          setScoreTouched(true);
          setScore(value === null || value === undefined ? null : Number(value));
        }}
      />
      {scoreMissing && <Typography.Text className={styles.noteRequiredHint}>Enter a score between 0 and 100.</Typography.Text>}
      <OverrideNoteField value={note} onChange={setNote} showRequiredHint={noteRequired} />
    </Modal>
  );
};

/** Reuses the DimensionScores display, mapping a Readiness pillar's `label` into `key` so the same row layout applies, plus a per-dimension Edit-modal override control. */
const ReadinessPillarCard: React.FC<{
  pillar: LivePillarScore;
  /** The persisted (no staged edits) version of this pillar — diffed against to highlight only rows whose score actually moved, not merely touched. */
  persistedPillar: LivePillarScore | undefined;
  /** Passed through to the edit modal to date each dimension's automated score. */
  computedAt: string;
  /** This pillar's share of the composite score, computed by the parent since it varies per submission. */
  pillarWeightPercent: number | null;
  staging: StagingState;
  onStageScore: (pillarKey: string, dimensionKey: string, score: number | null) => void;
  onStageNote: (pillarKey: string, dimensionKey: string, note: string) => void;
  onDiscardScore: (pillarKey: string, dimensionKey: string) => void;
}> = ({ pillar, persistedPillar, computedAt, pillarWeightPercent, staging, onStageScore, onStageNote, onDiscardScore }) => {
  const [editingDimensionKey, setEditingDimensionKey] = useState<string | null>(null);
  const editingDimension = pillar.dimensions.find((d) => d.key === editingDimensionKey);
  const editingStagedEntry = editingDimension ? staging[`${pillar.key}:${editingDimension.key}`] : undefined;
  const hasPillarScoreChanged = persistedPillar !== undefined && persistedPillar.pillarScore !== pillar.pillarScore;

  return (
    <Card
      title={
        <span>
          {pillar.label}
          {pillarWeightPercent !== null && (
            <Typography.Text type="secondary" className={styles.dimensionWeight}>
              {' '}
              ({pillarWeightPercent.toFixed(1)}% of overall score)
            </Typography.Text>
          )}
          <Tag className={cn(styles.competencyFlag, hasPillarScoreChanged && styles.pendingBadge)}>{pillar.pillarScore} / 100</Tag>
        </span>
      }
      className={styles.competencyCard}
    >
      <Typography.Paragraph type="secondary">{pillar.rationale}</Typography.Paragraph>
      <div className={styles.dimensions}>
        {pillar.dimensions.map((d) => {
          const key = `${pillar.key}:${d.key}`;
          const entry = staging[key];
          // Revert discards only this dimension's staged entry, gated on the entry itself, not the value diff below.
          const hasDirectStagedEdit = entry?.valueTouched ?? false;
          const persistedDimension = persistedPillar?.dimensions.find((pd) => pd.key === d.key);
          const hasScoreChanged = persistedDimension !== undefined && persistedDimension.score !== d.score;

          return (
            <div key={d.key} className={styles.dimension}>
              <div className={styles.dimensionHeader}>
                <Typography.Text strong>{d.label}</Typography.Text>
                <span className={styles.dimensionMeta}>
                  {d.weight !== null && (
                    <Typography.Text type="secondary" className={styles.dimensionWeight}>
                      {Math.round(d.weight * 100)}% weight
                    </Typography.Text>
                  )}
                  <Tag className={hasScoreChanged ? styles.pendingBadge : undefined}>{d.score} / 100</Tag>
                  <AntButton size="small" icon={<Pencil style={{ width: '1em', height: '1em' }} />} title="Edit" aria-label="Edit" onClick={() => setEditingDimensionKey(d.key)} />
                  {hasDirectStagedEdit && <RevertOverrideButton onClick={() => onDiscardScore(pillar.key, d.key)} />}
                </span>
              </div>
              <Typography.Text type="secondary">{d.rationale}</Typography.Text>
            </div>
          );
        })}
      </div>

      {editingDimension && (
        <DimensionEditModal
          pillarLabel={pillar.label}
          dimension={editingDimension}
          computedAt={computedAt}
          hasPendingScoreOverride={Boolean(editingStagedEntry?.valueTouched && editingStagedEntry.score !== null)}
          draftNote={editingStagedEntry?.note ?? ''}
          onClose={() => setEditingDimensionKey(null)}
          onSave={(score, scoreTouched, note) => {
            if (scoreTouched) onStageScore(pillar.key, editingDimension.key, score);
            onStageNote(pillar.key, editingDimension.key, note);
            setEditingDimensionKey(null);
          }}
        />
      )}
    </Card>
  );
};

interface AnswerNoteModalProps {
  answer: AdminHtsAnswerDetail;
  isSuitability: boolean;
  effectiveClassification: HtsAnswerClassification | null;
  /** Whether a staged classification change already exists, independent of this session — the revert button shows immediately on open. Never true for a persisted override. */
  hasPendingClassificationChange: boolean;
  /** Discards any staged-but-unreleased classification/note entry for this question — the revert button's action. */
  onDiscardClassification: () => void;
  effectiveHardGateFlagged: boolean;
  effectiveHardGatePriority: HtsFlagPriority | null;
  /** Same "staged, not-yet-released" meaning as hasPendingClassificationChange, just for the hard-gate path. */
  hasPendingHardGateChange: boolean;
  /** Stages immediately on toggle/select, independent of this modal's own Save — same discipline as ConsistencyFlagNoteModal's onStageDecision. */
  onStageHardGate: (flagged: boolean, priority: HtsFlagPriority | null) => void;
  /** Discards any staged-but-unreleased hard-gate/note entry for this question — the revert button's action. */
  onDiscardHardGate: () => void;
  draftNote: string;
  onSave: (note: string, classification: HtsAnswerClassification | null, classificationTouched: boolean) => void;
  onClose: () => void;
}

/** Opened by every answer row's Note button; suitability rows add a classification control, hard-gate rows the confirm/dismiss toggle + severity. */
const AnswerNoteModal: React.FC<AnswerNoteModalProps> = ({
  answer,
  isSuitability,
  effectiveClassification,
  hasPendingClassificationChange,
  onDiscardClassification,
  effectiveHardGateFlagged,
  effectiveHardGatePriority,
  hasPendingHardGateChange,
  onStageHardGate,
  onDiscardHardGate,
  draftNote,
  onSave,
  onClose,
}) => {
  const isHardGateFlagged = answer.isHardGate && answer.reviewFlagged;
  const [classification, setClassification] = useState<HtsAnswerClassification | null>(effectiveClassification);
  const [classificationTouched, setClassificationTouched] = useState(false);
  // Tracks only whether *this* modal session touched the toggle/select (which stage immediately) — mirrors classificationTouched.
  const [hardGateTouched, setHardGateTouched] = useState(false);
  const [note, setNote] = useState(draftNote);

  const topicLabel = answer.classificationTopic ? HTS_CLASSIFICATION_TOPIC_BY_ID[answer.classificationTopic]?.label : null;
  // Uses hasPending* props, not *Touched alone — reopening the modal resets Touched state, which
  // would otherwise let an emptied note strand a staged override with no note (rejected at Release).
  const noteRequired =
    (isSuitability && (classificationTouched || hasPendingClassificationChange) && classification !== null && !note.trim()) ||
    (isHardGateFlagged && (hardGateTouched || hasPendingHardGateChange) && !note.trim());

  // Automated classification/hard-gate state lives in classification/reviewFlagged, not history —
  // shown as the first row; a plain row has no automated verdict at all.
  const initialHistoryItem: HistoryItem | null = isSuitability
    ? {
        label: `Initial classification: ${answer.classification ? HTS_ANSWER_CLASSIFICATION_LABELS[answer.classification] : UNCLASSIFIED_LABEL}`,
        overriddenAt: answer.createdAt,
        overriddenByName: null,
      }
    : answer.isHardGate
      ? {
          label: answer.reviewFlagged
            ? `Initially flagged (${answer.flagPriority ?? HtsFlagPriority.STANDARD})`
            : 'Not initially flagged',
          overriddenAt: answer.createdAt,
          overriddenByName: null,
        }
      : null;

  const historyItems: HistoryItem[] = answer.overrideHistory.map((entry): HistoryItem => {
    if (entry.kind === HtsAnswerOverrideHistoryKind.Classification) {
      return {
        label:
          entry.classification === null
            ? 'Classification cleared (reverted to automatic)'
            : `Classification set to ${HTS_ANSWER_CLASSIFICATION_LABELS[entry.classification]}`,
        note: entry.note,
        overriddenAt: entry.overriddenAt,
        overriddenByName: entry.overriddenByName,
      };
    }
    if (entry.kind === HtsAnswerOverrideHistoryKind.HardGate) {
      return {
        label:
          entry.flagged === null
            ? 'Flag cleared (reverted to automatic)'
            : entry.flagged
              ? `Marked as confirmed (${entry.priority ?? HtsFlagPriority.STANDARD})`
              : 'Marked as dismissed',
        note: entry.note,
        overriddenAt: entry.overriddenAt,
        overriddenByName: entry.overriddenByName,
      };
    }
    return { label: 'Note added', note: entry.note, overriddenAt: entry.overriddenAt, overriddenByName: entry.overriddenByName };
  });
  if (initialHistoryItem) historyItems.unshift(initialHistoryItem);

  // Closing without Save must not leave a note-less hard-gate change staged (fails silently at
  // Release) — checked against draftNote, not live note, so an earlier saved note isn't discarded.
  const handleCancel = () => {
    if (hardGateTouched && !draftNote.trim()) onDiscardHardGate();
    onClose();
  };

  return (
    <Modal
      open
      title="Add a note"
      onCancel={handleCancel}
      footer={
        <div className={styles.modalFooterActions}>
          <Button appearance="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            appearance="solid"
            disabled={noteRequired}
            onClick={() => onSave(note, classification, classificationTouched)}
          >
            Save
          </Button>
        </div>
      }
    >
      <Typography.Paragraph type="secondary">
        {isSuitability
          ? "Add a note to this answer, and change its classification if the automated read needs correcting."
          : isHardGateFlagged
            ? 'Confirm or dismiss this hard-gate flag, and add a note explaining your review.'
            : 'Add a note to this answer.'}
      </Typography.Paragraph>

      <QAQuestion>{answer.questionText}</QAQuestion>
      <AnswerValue answer={answer} />

      <div className={styles.rowLabelTags}>
        <Typography.Text type="secondary" className={styles.tagsLabel}>
          Pillar(s):
        </Typography.Text>
        {isSuitability && <Tag>Integrity</Tag>}
        {isSuitability && topicLabel && <Tag color="blue">{topicLabel}</Tag>}
        {!isSuitability && !answer.isSensitive && answer.readinessPillars.map((p) => <Tag key={p}>{pillarLabelFor(p)}</Tag>)}
        {!isSuitability && (answer.isSensitive || answer.readinessPillars.length === 0) && (
          <Typography.Text type="secondary">None</Typography.Text>
        )}
      </div>

      {historyItems.length > 0 && (
        <>
          <Typography.Title level={5} className={styles.modalSectionTitle}>
            History
          </Typography.Title>
          <HistoryList items={historyItems} />
        </>
      )}

      {isSuitability && (
        <div className={styles.overrideControl}>
          <div className={styles.overrideControlRow}>
            <Select
              className={styles.classificationSelect}
              value={classification}
              placeholder={UNCLASSIFIED_LABEL}
              options={classificationSelectOptions}
              onChange={(value) => {
                setClassificationTouched(true);
                setClassification(value);
              }}
            />
            {(hasPendingClassificationChange || classificationTouched) && (
              <RevertOverrideButton
                onClick={() => {
                  onDiscardClassification();
                  // Persisted value, not effectiveClassification — that reflects the pending edit being discarded here.
                  setClassification(answer.overrideClassification ?? answer.classification);
                  setClassificationTouched(false);
                }}
              />
            )}
          </div>
          <Typography.Text className={styles.scoringHint}>{CLASSIFICATION_SCORING_HINT}</Typography.Text>
        </div>
      )}

      {isHardGateFlagged && (
        <div className={styles.overrideControl}>
          <div className={styles.overrideControlRow}>
            <FlagStandsToggle
              value={effectiveHardGateFlagged}
              onChange={(stands) => {
                setHardGateTouched(true);
                onStageHardGate(stands, stands ? (effectiveHardGatePriority ?? answer.flagPriority) : null);
              }}
            />
            <Select
              className={styles.prioritySelect}
              disabled={!effectiveHardGateFlagged}
              value={effectiveHardGatePriority}
              placeholder="Severity"
              options={[
                { value: HtsFlagPriority.STANDARD, label: 'Standard' },
                { value: HtsFlagPriority.HIGH, label: 'High' },
              ]}
              onChange={(value) => {
                setHardGateTouched(true);
                onStageHardGate(true, value);
              }}
            />
            {(hasPendingHardGateChange || hardGateTouched) && (
              <RevertOverrideButton
                onClick={() => {
                  onDiscardHardGate();
                  setHardGateTouched(false);
                }}
              />
            )}
          </div>
        </div>
      )}

      <OverrideNoteField value={note} onChange={setNote} showRequiredHint={noteRequired} />
    </Modal>
  );
};

interface AnswerRowProps {
  answer: AdminHtsAnswerDetail;
  staging: StagingState;
  onStageAnswerClassification: (questionId: string, classification: HtsAnswerClassification | null) => void;
  onStageHardGate: (questionId: string, flagged: boolean | null, priority: HtsFlagPriority | null) => void;
  onStageNote: (questionId: string, fallbackKind: StagedTargetKind, note: string) => void;
  /** Discards this question's staged entry — the revert button's action, shared since a question is never both suitability and hard-gate. */
  onDiscard: (questionId: string) => void;
  /** The staged-note kind for a plain row — `Answer` for a real HtsAnswer row, `Probe` for the synthetic perfect-person-probe row. */
  noteTargetKind?: StagedTargetKind;
  /** Set when the readiness scorer set this answer aside as not credible. */
  credibilityNote?: string;
  /** Absent when no follow-up can be added: a non-guest, the synthetic probe row, or a round already sent. */
  followUp?: AnswerFollowUpControls;
}

/** An answer row's follow-up drafting controls. `onChange(null)` removes the draft. */
interface AnswerFollowUpControls {
  draft: string | undefined;
  /** Why drafting is blocked; shown as the button's tooltip. */
  disabledReason: string | null;
  onChange: (text: string | null) => void;
}

const ProbeExchanges: React.FC<{ history: AdminHtsAnswerDetail['followUpHistory'] }> = ({ history }) => (
  <>
    {history.map((exchange, i) => (
      <div key={i} className={styles.probeExchange}>
        <QAQuestion>{exchange.question}</QAQuestion>
        <QAAnswer>{exchange.answer}</QAAnswer>
      </div>
    ))}
  </>
);

/** Drafts a follow-up question, shown with the guest's answer and probe transcript. Held on the page until sent. */
const FollowUpQuestionModal: React.FC<{
  open: boolean;
  answer: AdminHtsAnswerDetail;
  initial: string;
  onSave: (text: string) => void;
  onClose: () => void;
}> = ({ open, answer, initial, onSave, onClose }) => {
  const [text, setText] = useState(initial);
  // Stays mounted to animate closed; each opening starts from the saved draft.
  useEffect(() => {
    if (open) setText(initial);
  }, [open, initial]);
  return (
    <Modal
      open={open}
      destroyOnHidden
      centered
      title="Follow-up question"
      onCancel={onClose}
      footer={
        <div className={styles.modalFooterActions}>
          <Button appearance="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="solid" disabled={!text.trim()} onClick={() => onSave(text.trim())}>
            Save
          </Button>
        </div>
      }
    >
      <Typography.Paragraph type="secondary">
        The guest answers this once, together with any other follow-ups you send. It isn&apos;t sent until you click Send follow-up questions.
      </Typography.Paragraph>
      <QAQuestion>{answer.questionText}</QAQuestion>
      <AnswerValue answer={answer} />
      {answer.followUpHistory.length > 0 && (
        <div className={styles.followUpModalProbes}>
          <ProbeExchanges history={answer.followUpHistory} />
        </div>
      )}
      <Input.TextArea
        className={styles.followUpInput}
        autoSize={{ minRows: 3, maxRows: 8 }}
        maxLength={MAX_ANSWER_MESSAGE_LENGTH}
        placeholder="Write your follow-up question…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    </Modal>
  );
};

const AnswerRow: React.FC<AnswerRowProps> = ({
  answer,
  staging,
  onStageAnswerClassification,
  onStageHardGate,
  onStageNote,
  onDiscard,
  noteTargetKind = StagedTargetKind.Answer,
  credibilityNote,
  followUp,
}) => {
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const entry = staging[answer.questionId];
  const isSuitability = isSuitabilityTopic(answer.classificationTopic);

  const effectiveClassification = isSuitability ? effectiveClassificationFor(answer, entry) : null;
  const { effectivePriority, effectiveReviewFlagged: isEffectivelyFlagged } = effectiveFlagFor(answer, entry);
  const effectiveHardGateFlagged = answer.isHardGate && isEffectivelyFlagged;
  const effectiveHardGatePriority = effectiveHardGateFlagged ? effectivePriority : null;
  const pendingValueChange = entry?.valueTouched ?? false;

  const hasNote = Boolean(answer.overrideNote || (entry?.note ?? '').trim());

  return (
    <div className={cn(styles.responseCard, isEffectivelyFlagged && styles.flaggedRow)}>
      <div className={styles.responseHeader}>
        <QAQuestion>{answer.questionText}</QAQuestion>
        <span className={styles.responseHeaderTags}>
          <AntButton
            size="small"
            icon={<FileText style={{ width: '1em', height: '1em' }} />}
            title={hasNote ? 'Edit note' : 'Add note'}
            aria-label={hasNote ? 'Edit note' : 'Add note'}
            onClick={() => setNoteModalOpen(true)}
          />
          {followUp && (
            <AntButton
              size="small"
              icon={<MessageCircleQuestion style={{ width: '1em', height: '1em' }} />}
              title={followUp.disabledReason ?? (followUp.draft ? 'Edit follow-up question' : 'Add follow-up question')}
              aria-label="Add follow-up question"
              disabled={followUp.disabledReason !== null}
              onClick={() => setFollowUpModalOpen(true)}
            />
          )}
        </span>
      </div>

      <div className={styles.rowLabelTags}>
        {answer.isSensitive && <Tag color="purple">SENSITIVE</Tag>}
        {isSuitability && (
          <Tag
            color={effectivePriority === HtsFlagPriority.HIGH ? 'error' : effectivePriority === HtsFlagPriority.STANDARD ? 'warning' : undefined}
            className={pendingValueChange ? styles.pendingBadge : undefined}
          >
            {effectiveClassification ? HTS_ANSWER_CLASSIFICATION_LABELS[effectiveClassification] : UNCLASSIFIED_LABEL}
            {effectivePriority ? ` (${effectivePriority})` : ''}
          </Tag>
        )}
        {answer.isHardGate && effectiveHardGateFlagged && (
          <Tag color={effectiveHardGatePriority === HtsFlagPriority.HIGH ? 'error' : 'warning'} className={pendingValueChange ? styles.pendingBadge : undefined}>
            Hard gate ({effectiveHardGatePriority ?? HtsFlagPriority.STANDARD})
          </Tag>
        )}
      </div>

      <AnswerValue answer={answer} />

      {answer.reviewReasons.length > 0 && (
        <div className={styles.reviewReasons}>
          <TriangleAlert className={styles.warningIcon} style={{ width: '1em', height: '1em' }} />
          {answer.reviewReasons.map((r, i) => (
            <Typography.Text key={i} type="danger" className={styles.reviewReason}>
              {r}
            </Typography.Text>
          ))}
        </div>
      )}

      {credibilityNote && (
        <div className={styles.reviewReasons}>
          <TriangleAlert className={styles.warningIcon} style={{ width: '1em', height: '1em' }} />
          <Typography.Text type="danger" className={styles.reviewReason}>
            Credibility check: {credibilityNote}
          </Typography.Text>
        </div>
      )}

      {answer.followUpHistory.length > 0 && (
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: 'probes',
              label: `Probe transcript (${answer.followUpHistory.length})`,
              children: <ProbeExchanges history={answer.followUpHistory} />,
            },
          ]}
        />
      )}

      {answer.adminFollowUp && (
        <div className={styles.followUpBlock}>
          <Typography.Text type="secondary" className={styles.followUpDraftLabel}>
            Admin follow-up
          </Typography.Text>
          <Typography.Text italic className={styles.adminFollowUpQuestion}>
            {answer.adminFollowUp.question}
          </Typography.Text>
          {answer.adminFollowUp.answer !== null ? (
            <QAAnswer>{answer.adminFollowUp.answer}</QAAnswer>
          ) : (
            <Typography.Text type="secondary">Awaiting answer</Typography.Text>
          )}
        </div>
      )}

      {followUp?.draft && (
        <div className={cn(styles.followUpBlock, styles.followUpDraft)}>
          <Typography.Text type="secondary" className={styles.followUpDraftLabel}>
            Follow-up question (not sent)
          </Typography.Text>
          <div className={styles.followUpDraftRow}>
            <Typography.Text italic className={styles.adminFollowUpQuestion}>
              {followUp.draft}
            </Typography.Text>
            <span className={styles.responseHeaderTags}>
              <AntButton
                size="small"
                icon={<Pencil style={{ width: '1em', height: '1em' }} />}
                title="Edit follow-up question"
                aria-label="Edit follow-up question"
                disabled={followUp.disabledReason !== null}
                onClick={() => setFollowUpModalOpen(true)}
              />
              <AntButton
                size="small"
                danger
                icon={<Trash2 style={{ width: '1em', height: '1em' }} />}
                title="Remove follow-up question"
                aria-label="Remove follow-up question"
                onClick={() => followUp.onChange(null)}
              />
            </span>
          </div>
        </div>
      )}

      {followUp && (
        <FollowUpQuestionModal
          open={followUpModalOpen}
          answer={answer}
          initial={followUp.draft ?? ''}
          onSave={(text) => {
            followUp.onChange(text);
            setFollowUpModalOpen(false);
          }}
          onClose={() => setFollowUpModalOpen(false)}
        />
      )}

      {noteModalOpen && (
        <AnswerNoteModal
          answer={answer}
          isSuitability={isSuitability}
          effectiveClassification={effectiveClassification}
          hasPendingClassificationChange={pendingValueChange}
          onDiscardClassification={() => onDiscard(answer.questionId)}
          effectiveHardGateFlagged={effectiveHardGateFlagged}
          effectiveHardGatePriority={effectiveHardGatePriority}
          hasPendingHardGateChange={pendingValueChange}
          onStageHardGate={(flagged, priority) => onStageHardGate(answer.questionId, flagged, priority)}
          onDiscardHardGate={() => onDiscard(answer.questionId)}
          draftNote={entry?.note ?? ''}
          onClose={() => setNoteModalOpen(false)}
          onSave={(note, classification, classificationTouched) => {
            if (classificationTouched) onStageAnswerClassification(answer.questionId, classification);
            onStageNote(answer.questionId, noteTargetKind, note);
            setNoteModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

interface ConsistencyFlagCardProps {
  flag: AdminHtsConsistencyFlagDetail;
  staging: StagingState;
  onStageDecision: (flagId: string, decision: HtsConsistencyFlagOverrideDecision | null) => void;
  onStageNote: (flagId: string, note: string) => void;
  onDiscard: (flagId: string) => void;
}

interface ConsistencyFlagNoteModalProps {
  flag: AdminHtsConsistencyFlagDetail;
  standsEffectively: boolean;
  /** Whether this flag has a staged decision change — never true for a persisted override, so the revert button disappears once staging clears. */
  hasPendingDecisionChange: boolean;
  onStageDecision: (decision: HtsConsistencyFlagOverrideDecision | null) => void;
  /** Discards the staged-but-unreleased decision/note entry — the revert button's action. */
  onDiscard: () => void;
  draftNote: string;
  onSave: (note: string) => void;
  onClose: () => void;
}

/** Opened by a consistency flag's Note button — decision and note edited together; the toggle stages immediately, independent of Save. */
const ConsistencyFlagNoteModal: React.FC<ConsistencyFlagNoteModalProps> = ({
  flag,
  standsEffectively,
  hasPendingDecisionChange,
  onStageDecision,
  onDiscard,
  draftNote,
  onSave,
  onClose,
}) => {
  const [note, setNote] = useState(draftNote);
  // Tracks only whether *this* modal session touched the toggle (which stages immediately) — same discipline as AnswerNoteModal's hardGateTouched.
  const [decisionTouched, setDecisionTouched] = useState(false);
  // Uses hasPendingDecisionChange, not decisionTouched alone — same reason as AnswerNoteModal's noteRequired.
  const noteRequired = (decisionTouched || hasPendingDecisionChange) && !note.trim();

  // The detected contradiction lives in flag.note, not history — shown as the first row.
  const historyItems: HistoryItem[] = [
    { label: 'Automatically flagged', note: flag.note, overriddenAt: flag.createdAt, overriddenByName: null },
  ];
  historyItems.push(...flag.overrideHistory.map((entry): HistoryItem => {
    if (entry.kind === HtsConsistencyFlagOverrideHistoryKind.Decision) {
      return {
        label:
          entry.decision === null
            ? 'Decision cleared (reverted to automatic)'
            : entry.decision === HtsConsistencyFlagOverrideDecision.VALID
              ? 'Marked as confirmed'
              : 'Marked as dismissed',
        note: entry.note,
        overriddenAt: entry.overriddenAt,
        overriddenByName: entry.overriddenByName,
      };
    }
    return { label: 'Note added', note: entry.note, overriddenAt: entry.overriddenAt, overriddenByName: entry.overriddenByName };
  }));

  // Same discipline as AnswerNoteModal's handleCancel — don't strand a note-less staged decision, but don't discard an already-noted one.
  const handleCancel = () => {
    if (decisionTouched && !draftNote.trim()) onDiscard();
    onClose();
  };

  return (
    <Modal
      open
      title="Add a note"
      onCancel={handleCancel}
      footer={
        <div className={styles.modalFooterActions}>
          <Button appearance="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button appearance="solid" disabled={noteRequired} onClick={() => onSave(note)}>
            Save
          </Button>
        </div>
      }
    >
      <Typography.Paragraph type="secondary">
        Confirm or dismiss this consistency flag, and add a note explaining your review.
      </Typography.Paragraph>

      <div className={styles.rowLabelTags}>
        <Tag>Affects: Integrity - Honesty &amp; Completeness</Tag>
      </div>

      {historyItems.length > 0 && (
        <>
          <Typography.Title level={5} className={styles.modalSectionTitle}>
            History
          </Typography.Title>
          <HistoryList items={historyItems} />
        </>
      )}

      <div className={styles.overrideControl}>
        <div className={styles.overrideControlRow}>
          <FlagStandsToggle
            value={standsEffectively}
            onChange={(stands) => {
              setDecisionTouched(true);
              onStageDecision(stands ? HtsConsistencyFlagOverrideDecision.VALID : HtsConsistencyFlagOverrideDecision.INVALID);
            }}
          />
          {(hasPendingDecisionChange || decisionTouched) && (
            <RevertOverrideButton
              onClick={() => {
                onDiscard();
                setDecisionTouched(false);
              }}
            />
          )}
        </div>
        <Typography.Text className={styles.scoringHint}>{CONSISTENCY_FLAG_SCORING_HINT}</Typography.Text>
      </div>

      <OverrideNoteField value={note} onChange={setNote} showRequiredHint={noteRequired} />
    </Modal>
  );
};

const ConsistencyFlagCard: React.FC<ConsistencyFlagCardProps> = ({ flag, staging, onStageDecision, onStageNote, onDiscard }) => {
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const entry = staging[flag.id];
  const effectiveDecision = effectiveConsistencyDecisionFor(flag, entry);
  const pendingValueChange = entry?.valueTouched ?? false;
  // No override means the flag still holds — null reads as "stands", matching the hard-gate fallback.
  const standsEffectively = effectiveDecision !== HtsConsistencyFlagOverrideDecision.INVALID;
  const hasNote = Boolean(flag.overrideNote || (entry?.note ?? '').trim());

  return (
    <div className={styles.consistencyFlag}>
      <div className={styles.responseHeader}>
        <div className={styles.rowLabelTags}>
          <Tag>Affects: Integrity - Honesty &amp; Completeness</Tag>
          {effectiveDecision && (
            <Tag className={pendingValueChange ? styles.pendingBadge : undefined}>
              {effectiveDecision === HtsConsistencyFlagOverrideDecision.INVALID ? 'Dismissed' : 'Confirmed'}
            </Tag>
          )}
        </div>
        <AntButton
          size="small"
          icon={<FileText style={{ width: '1em', height: '1em' }} />}
          title={hasNote ? 'Edit note' : 'Add note'}
          aria-label={hasNote ? 'Edit note' : 'Add note'}
          onClick={() => setNoteModalOpen(true)}
        />
      </div>
      <Typography.Text type="warning" className={styles.reviewReason}>
        {flag.note}
      </Typography.Text>

      {noteModalOpen && (
        <ConsistencyFlagNoteModal
          flag={flag}
          standsEffectively={standsEffectively}
          hasPendingDecisionChange={pendingValueChange}
          onStageDecision={(decision) => onStageDecision(flag.id, decision)}
          onDiscard={() => onDiscard(flag.id)}
          draftNote={entry?.note ?? ''}
          onClose={() => setNoteModalOpen(false)}
          onSave={(note) => {
            onStageNote(flag.id, note);
            setNoteModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

interface NarrativeReviewModalProps {
  /** The editable draft — starts as the preview call's narrative (or the existing one, unchanged, when no redraft was needed). */
  narrative: AdminHtsReadinessNarrative;
  onChange: (narrative: AdminHtsReadinessNarrative) => void;
  /** Oldest to newest — rendered below the editor, source-attributed. */
  history: AdminHtsReadinessNarrativeHistoryEntry[];
  band: HtsReadinessBand | undefined;
  /** Pillar-label tags from getHtsReadinessSummaryForCandidate ("Where you're strong" / "Where to focus next"); read-only, derived from live pillar scores. */
  strongPillarLabels: string[];
  focusPillarLabels: string[];
  /** True when this screen's narrative is a fresh model draft (a dimension score changed this batch), for the explanatory copy at the top. */
  regenerated: boolean;
  /** False only when this submission has never been visible to the candidate before — drives the first-release/will-change-what-they-see alerts below. */
  alreadyVisibleToCandidate: boolean;
  /** An access-code guest never sees their results — swaps the candidate-facing copy for agency-facing copy. */
  isGuest: boolean;
  /** False with no Readiness Profile (no scoreable pillar) — hides every narrative-editing field, since there's nowhere to persist one and a non-empty finalNarrative would 400. */
  hasProfile: boolean;
  /** True while a submit-time scoring failure is unresolved — Release stays disabled until `scoringFailureAcknowledged` is ticked. */
  scoringFailureUnresolved: boolean;
  scoringFailureAcknowledged: boolean;
  onScoringFailureAcknowledgedChange: (acknowledged: boolean) => void;
  committing: boolean;
  saveError: string | null;
  onBack: () => void;
  onCancel: () => void;
  onRelease: () => void;
}

/** Step 2 of the release modal (after the preview step) — the editable candidate-facing narrative plus history log; coachSummary (admin-only) sits below a divider. */
const NarrativeReviewModal: React.FC<NarrativeReviewModalProps> = ({
  narrative,
  onChange,
  history,
  band,
  strongPillarLabels,
  focusPillarLabels,
  regenerated,
  alreadyVisibleToCandidate,
  isGuest,
  hasProfile,
  scoringFailureUnresolved,
  scoringFailureAcknowledged,
  onScoringFailureAcknowledgedChange,
  committing,
  saveError,
  onBack,
  onCancel,
  onRelease,
}) => {
  const historyItems: HistoryItem[] = history.map((entry) => ({
    label: entry.source === HtsReadinessNarrativeSource.Model ? 'Model regenerated the readiness profile' : 'Manually edited',
    overriddenAt: entry.recordedAt,
    overriddenByName: entry.triggeredByName,
  }));

  // The three candidate-facing fields — blank renders as an empty card on their Readiness page,
  // so Release is blocked until each has content (matches the API's check on finalNarrative).
  // coachSummary is exempt, as is all of this with no profile — nothing here gets persisted
  // then (see saveHtsAdminReviewCore's no-profile early return).
  const strengthsMissing = hasProfile && !narrative.candidateSummary.strengths.trim();
  const prioritiesMissing = hasProfile && !narrative.candidateSummary.priorities.trim();
  const bottomLineMissing = hasProfile && !narrative.candidateSummary.bottomLine.trim();
  const candidateSummaryIncomplete = strengthsMissing || prioritiesMissing || bottomLineMissing;
  const releaseDisabled = candidateSummaryIncomplete || (scoringFailureUnresolved && !scoringFailureAcknowledged);

  return (
    <Modal
      open
      title="Review readiness profile"
      onCancel={onCancel}
      footer={
        <div className={styles.modalFooterActions}>
          <Button appearance="outline" onClick={onBack} disabled={committing}>
            Back
          </Button>
          <Button appearance="solid" loading={committing} disabled={releaseDisabled} onClick={onRelease}>
            Release
          </Button>
        </div>
      }
    >
      {hasProfile ? (
        <Typography.Paragraph type="secondary">
          {isGuest
            ? 'Guest submission: this is what the agency receives; the candidate never sees it.'
            : 'This is what the candidate will see once released.'}
          {regenerated && ' The model just redrafted it from the updated scores.'} Edit any field before releasing.
        </Typography.Paragraph>
      ) : (
        <Typography.Paragraph type="secondary">
          This submission has no Readiness Profile — no scoreable pillar was ever computed for it, so there's no
          score, band or narrative to release. Saving here only persists your notes above.
        </Typography.Paragraph>
      )}

      {scoringFailureUnresolved && (
        <Alert
          type="warning"
          showIcon
          className={styles.provisionalAlert}
          message="Automated scoring failed for this submission"
          description={
            <>
              <Typography.Paragraph>
                Every affected dimension shows a placeholder score of 0 with the rationale &quot;No data available
                for this dimension.&quot; — not a real score. Review those dimensions before releasing.
              </Typography.Paragraph>
              <Checkbox
                checked={scoringFailureAcknowledged}
                onChange={(e) => onScoringFailureAcknowledgedChange(e.target.checked)}
              >
                I&apos;ve reviewed the placeholder scores and confirmed the scoring failure has been dealt with.
              </Checkbox>
            </>
          }
        />
      )}

      {hasProfile && !isGuest && !alreadyVisibleToCandidate && (
        <Alert
          type="info"
          showIcon
          message="This is this submission's first-ever release to the candidate."
          className={styles.provisionalAlert}
        />
      )}
      {hasProfile && !isGuest && alreadyVisibleToCandidate && regenerated && (
        <Alert
          type="warning"
          showIcon
          message="This will change what the candidate currently sees in their readiness profile."
          className={styles.provisionalAlert}
        />
      )}

      {band !== undefined && (
        <div className={styles.confirmScoreRow}>
          <Typography.Text strong>Overall readiness score range</Typography.Text>
          <Tag>{bandLabelFor(band)}</Tag>
        </div>
      )}

      {hasProfile && (
        <>
          <Typography.Title level={5} className={styles.modalSectionTitle}>
            Where you&apos;re strong
          </Typography.Title>
          <div className={styles.tagRow}>
            {strongPillarLabels.map((label) => (
              <Tag key={label} color="success">{label}</Tag>
            ))}
            <Typography.Text type="secondary" className={styles.scoringHint}>
              (Admin note: this submission&apos;s {HTS_STRONG_PILLAR_COUNT} highest-scoring pillars.)
            </Typography.Text>
          </div>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 8 }}
            value={narrative.candidateSummary.strengths}
            onChange={(e) => onChange({ ...narrative, candidateSummary: { ...narrative.candidateSummary, strengths: e.target.value } })}
          />
          {strengthsMissing && (
            <Typography.Text className={styles.noteRequiredHint}>
              {isGuest ? 'The agency receives' : 'The candidate sees'} this section — it can&apos;t be released empty.
            </Typography.Text>
          )}

          <Typography.Title level={5} className={styles.modalSectionTitle}>
            Where to focus next
          </Typography.Title>
          <div className={styles.tagRow}>
            {focusPillarLabels.map((label) => (
              <Tag key={label} color="warning">{label}</Tag>
            ))}
            <Typography.Text type="secondary" className={styles.scoringHint}>
              (Admin note: every pillar scoring below {HTS_PRIORITY_PILLAR_SCORE_THRESHOLD}.)
            </Typography.Text>
          </div>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 8 }}
            value={narrative.candidateSummary.priorities}
            onChange={(e) => onChange({ ...narrative, candidateSummary: { ...narrative.candidateSummary, priorities: e.target.value } })}
          />
          {prioritiesMissing && (
            <Typography.Text className={styles.noteRequiredHint}>
              {isGuest ? 'The agency receives' : 'The candidate sees'} this section — it can&apos;t be released empty.
            </Typography.Text>
          )}

          <Typography.Title level={5} className={styles.modalSectionTitle}>
            The bottom line
          </Typography.Title>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 8 }}
            value={narrative.candidateSummary.bottomLine}
            onChange={(e) => onChange({ ...narrative, candidateSummary: { ...narrative.candidateSummary, bottomLine: e.target.value } })}
          />
          {bottomLineMissing && (
            <Typography.Text className={styles.noteRequiredHint}>
              {isGuest ? 'The agency receives' : 'The candidate sees'} this section — it can&apos;t be released empty.
            </Typography.Text>
          )}

          <div className={styles.narrativeAdminOnly}>
            <Typography.Title level={5} className={styles.modalSectionTitle}>
              Coach summary
            </Typography.Title>
            <Typography.Text type="secondary">Admin only — the candidate does not see this.</Typography.Text>
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 8 }}
              value={narrative.coachSummary}
              onChange={(e) => onChange({ ...narrative, coachSummary: e.target.value })}
            />
          </div>
        </>
      )}

      {historyItems.length > 0 && (
        <>
          <Typography.Title level={5} className={styles.modalSectionTitle}>
            Narrative history
          </Typography.Title>
          <HistoryList items={historyItems} />
        </>
      )}

      {saveError && <Alert type="error" showIcon message={saveError} className={styles.provisionalAlert} />}
    </Modal>
  );
};

/** What the parent's Release button needs to render itself (see HtsSectionHandle) — reported even with no Readiness Profile, since staged notes alone can enable release. */
export interface HtsReleaseStatus {
  stagedCount: number;
  answerOverrideCount: number;
  dimensionOverrideCount: number;
  flagOverrideCount: number;
  noteOnlyCount: number;
  canRelease: boolean;
}

/** Imperative handle so the parent page's own Release button (rendered up in its title row, not here) can open the confirm modal this component owns. */
export interface HtsSectionHandle {
  openRelease: () => void;
  /** Opens the confirm modal for sending the drafted follow-ups, or confirming there are none. */
  openSendFollowUps: () => void;
}

interface HtsSectionProps {
  hts: AdminHtsSubmissionDetail;
  submissionId: string;
  onSaved: (updated: AdminHtsSubmissionDetail) => void;
  /** Called whenever release status (staged counts, whether release is allowed) changes, so the parent's Release button doesn't need to duplicate this component's staging state. */
  onReleaseStatusChange?: (status: HtsReleaseStatus | null) => void;
  /** Reports the number of non-blank follow-up drafts, for the parent's header button label. */
  onFollowUpDraftCountChange?: (count: number) => void;
}

/**
 * Full HTS review page — answers, consistency flags, perfect-person probe and
 * Readiness Profile, with override/note/release controls layered on. Nothing
 * reaches the server until Save/Release; staged edits live in `staging` and
 * are discarded on unmount. The Release button itself lives in the parent's
 * title row — `onReleaseStatusChange` feeds it, `openRelease` (via `ref`) triggers it.
 */
export const HtsSection = forwardRef<HtsSectionHandle, HtsSectionProps>(({ hts, submissionId, onSaved, onReleaseStatusChange, onFollowUpDraftCountChange }, ref) => {
  const { getToken } = useAuth();
  const { notification } = App.useApp();

  const [staging, setStaging] = useState<StagingState>({});
  const [adminNoteDraft, setAdminNoteDraft] = useState(hts.readinessProfile?.adminNote ?? '');
  const [savingAdminNote, setSavingAdminNote] = useState(false);

  // Follow-up drafts by questionId; page state, lost on reload.
  const [followUpDrafts, setFollowUpDrafts] = useState<Record<string, string>>({});
  const [sendFollowUpsOpen, setSendFollowUpsOpen] = useState(false);
  const [sendingFollowUps, setSendingFollowUps] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Narrative review step — see NarrativeReviewModal's doc comment ──
  const [reviewStep, setReviewStep] = useState<'changes' | 'loading' | 'narrative'>('changes');
  const [narrativePreview, setNarrativePreview] = useState<AdminHtsReadinessNarrativePreview | null>(null);
  const [narrativeDraft, setNarrativeDraft] = useState<AdminHtsReadinessNarrative | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Required while readinessProfile.scoringFailureUnresolved is true — see
  // NarrativeReviewModal's prop of the same name.
  const [scoringFailureAcknowledged, setScoringFailureAcknowledged] = useState(false);

  const { readinessProfile } = hts;

  const stageValue = (key: string, kind: StagedTargetKind, patch: Partial<StagedEntry>) => {
    setStaging((prev) => ({ ...prev, [key]: { ...prev[key], kind, valueTouched: true, note: prev[key]?.note ?? '', ...patch } }));
  };

  const stageNote = (key: string, fallbackKind: StagedTargetKind, note: string) => {
    setStaging((prev) => ({
      ...prev,
      [key]: { ...prev[key], kind: prev[key]?.kind ?? fallbackKind, valueTouched: prev[key]?.valueTouched ?? false, note },
    }));
  };

  /** Discards a staged edit, falling back to whatever's persisted — not a "clear to null" that would force the baseline value even over a saved override. */
  const discardStagedEntry = (key: string) => {
    setStaging((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const stagedCount = useMemo(
    () => Object.values(staging).filter((e) => e.valueTouched || e.note.trim()).length,
    [staging]
  );

  const liveEffective = useMemo(() => computeLiveEffectiveProfile(hts, staging), [hts, staging]);
  // Persisted (no staged edits) baseline, diffed against liveEffective to decide the pending highlight — see ReadinessPillarCard's persistedPillar doc.
  const persistedEffective = useMemo(() => computeLiveEffectiveProfile(hts, {}), [hts]);
  // Each present pillar's share of the total compositeWeight present — see
  // ReadinessPillarCard's pillarWeightPercent doc.
  const pillarWeightPercentByKey = useMemo(
    () => computePillarWeightPercents(readinessProfile?.presentPillars ?? []),
    [readinessProfile?.presentPillars]
  );

  // What getHtsReadinessSummaryForCandidate would compute right now off live pillar scores —
  // previews the exact "strong"/"focus" tags, not just the prose.
  const strongPillarLabels = liveEffective
    ? [...liveEffective.pillars].sort((a, b) => b.pillarScore - a.pillarScore).slice(0, HTS_STRONG_PILLAR_COUNT).map((p) => p.label)
    : [];
  const credibilityNoteByQuestionId = new Map(
    (readinessProfile?.detail?.credibilityConcerns ?? []).map((c) => [c.questionId, c.note])
  );
  const focusPillarLabels = liveEffective
    ? liveEffective.pillars.filter((p) => p.pillarScore < HTS_PRIORITY_PILLAR_SCORE_THRESHOLD).map((p) => p.label)
    : [];

  // An access-code guest never sees their results (see getHtsCandidateSummaryStatus) — only the copy differs for one.
  const isGuest = hts.accessCodeTags !== null;

  // One round per submission, guests only; the add buttons disappear once it's sent.
  const canDraftFollowUps = hts.isGuest && hts.adminFollowUps === null;
  const followUpDisabledReason =
    hts.guestLoginEndsAt && new Date(hts.guestLoginEndsAt).getTime() <= Date.now()
      ? `This guest can no longer log in (access ended ${formatDate(hts.guestLoginEndsAt)})`
      : null;
  const followUpDraftEntries = Object.entries(followUpDrafts).filter(([, text]) => text.trim().length > 0);
  const followUpDraftCount = followUpDraftEntries.length;
  const setFollowUpDraft = (questionId: string, text: string | null) =>
    setFollowUpDrafts((prev) => {
      const next = { ...prev };
      if (text === null) delete next[questionId];
      else next[questionId] = text;
      return next;
    });
  const followUpControlsFor = (questionId: string): AnswerFollowUpControls | undefined =>
    canDraftFollowUps
      ? {
          draft: followUpDrafts[questionId],
          disabledReason: followUpDisabledReason,
          onChange: (text) => setFollowUpDraft(questionId, text),
        }
      : undefined;

  const handleSendFollowUps = async () => {
    setSendingFollowUps(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const updated = await new AdminService(token).sendHtsAdminFollowUps(
        submissionId,
        followUpDraftEntries.map(([questionId, question]) => ({ questionId, question: question.trim() }))
      );
      setSendFollowUpsOpen(false);
      onSaved(updated);
      notification.success({
        message: followUpDraftCount > 0 ? 'Follow-up questions sent' : 'Confirmed: no follow-up questions needed',
      });
    } catch (e) {
      console.error(e);
      notification.error({ message: e instanceof Error ? e.message : 'Failed to send follow-up questions' });
    } finally {
      setSendingFollowUps(false);
    }
  };

  // The review gate alone, guest or not; true with no profile since nothing is withheld then.
  const alreadyVisibleToCandidate = readinessProfile ? !isHtsReviewPending(readinessProfile) : true;

  // ── Withheld banner ───────────────────────────────────────────────────────
  // Shown only while withheld; lists provisionalReasons (one per mandatory-review
  // trigger). Disappears on release since the banner answers "is this currently
  // kept back" — the triggers/flags themselves stay visible elsewhere. A
  // pre-existing row may have an empty array until its next recompute.
  const isPendingReview = !alreadyVisibleToCandidate;
  const withheldReasons = readinessProfile?.provisionalReasons ?? [];

  // Enabled with a staged batch, a pending first release, or any profile to review — an admin
  // can edit the narrative directly with no score change, so "nothing staged" isn't "nothing to do".
  const canSaveRelease = stagedCount > 0 || !alreadyVisibleToCandidate || Boolean(readinessProfile);

  const handleSaveAdminNote = async () => {
    setSavingAdminNote(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      // Hands the persisted note + fresh attribution back to the parent, same as handleConfirmSave — else this panel keeps rendering stale values.
      onSaved(await new AdminService(token).updateHtsAdminNote(submissionId, adminNoteDraft));
      notification.success({ message: 'Reviewer note saved' });
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to save reviewer note' });
    } finally {
      setSavingAdminNote(false);
    }
  };

  /** Opens the release modal; with nothing staged, skips the changes-diff screen (it'd show all zeros) straight to the narrative screen. */
  const handleOpenConfirm = () => {
    setSaveError(null);
    setNarrativePreview(null);
    setNarrativeDraft(null);
    setScoringFailureAcknowledged(false);
    setConfirmOpen(true);
    if (stagedCount === 0) {
      setReviewStep('loading');
      void handlePreviewNarrative();
    } else {
      setReviewStep('changes');
    }
  };

  /** Back to the changes step — clears the loaded preview so a further batch edit can't release alongside a stale narrative (see previewHtsReadinessNarrative's staleness note). */
  const handleBackToChanges = () => {
    setNarrativePreview(null);
    setNarrativeDraft(null);
    setScoringFailureAcknowledged(false);
    setReviewStep('changes');
  };

  const handlePreviewNarrative = async () => {
    setPreviewLoading(true);
    setSaveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const batch = buildReviewBatch(staging);
      const preview = await new AdminService(token).previewHtsReadinessNarrative(submissionId, batch);
      // No redraft happened, so `preview.narrative` still describes the
      // pre-override scores. Advancing would show it under "the model just
      // redrafted it" and log a model-authored history entry for text the
      // model never wrote — stay put instead, on the step whose own retry
      // control re-runs this.
      if (preview.redraftFailed) {
        setSaveError('Could not redraft the readiness profile — the model call failed. Try again.');
        return;
      }
      setNarrativePreview(preview);
      setNarrativeDraft(
        preview.narrative ?? {
          coachSummary: '',
          candidateSummary: { strengths: '', priorities: '', bottomLine: '' },
        },
      );
      setReviewStep('narrative');
    } catch (e) {
      console.error(e);
      setSaveError(e instanceof Error ? e.message : 'Failed to preview readiness profile');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmSave = async () => {
    setCommitting(true);
    setSaveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const batch = buildReviewBatch(staging, scoringFailureAcknowledged);
      const generatedNarrative = narrativePreview?.needsNarrativeRedraft ? narrativePreview.narrative : undefined;
      // With no profile there's nothing to persist a narrative against — send
      // null, since the placeholder draft would fail finalNarrative's
      // non-empty-fields validation.
      const finalNarrative = readinessProfile ? narrativeDraft : null;
      const result = await new AdminService(token).saveHtsAdminReview(submissionId, batch, finalNarrative, generatedNarrative);
      onSaved(result);
      setStaging({});
      setConfirmOpen(false);
      setReviewStep('changes');
      setNarrativePreview(null);
      setNarrativeDraft(null);
      setScoringFailureAcknowledged(false);
      notification.success({ message: 'Readiness profile released' });
    } catch (e) {
      console.error(e);
      setSaveError(e instanceof Error ? e.message : 'Failed to save review');
    } finally {
      setCommitting(false);
    }
  };

  // Rendered as the last "All submission answers" row — a plain AnswerRow targeting the Probe staging kind since it has no real HtsAnswer.
  const perfectPersonProbeAnswer: AdminHtsAnswerDetail | null = hts.perfectPersonProbe
    ? {
        questionId: HTS_PERFECT_PERSON_PROBE_ID,
        order: Number.MAX_SAFE_INTEGER,
        section: '',
        questionText: hts.perfectPersonProbe.question,
        value: hts.perfectPersonProbe.answer,
        isSensitive: false,
        classification: null,
        reviewFlagged: false,
        flagPriority: null,
        reviewReasons: [],
        followUpRound: 0,
        followUpHistory: [],
        createdAt: hts.perfectPersonProbe.noteUpdatedAt ?? '',
        classificationTopic: null,
        readinessPillars: [],
        isHardGate: false,
        groupFields: null,
        overrideClassification: null,
        overrideFlagged: null,
        overridePriority: null,
        // Mapped from the probe's note fields into AdminHtsAnswerDetail's override shape so AnswerRow/AnswerNoteModal need no probe-specific branch.
        overrideNote: hts.perfectPersonProbe.note,
        overriddenAt: hts.perfectPersonProbe.noteUpdatedAt,
        overriddenByName: hts.perfectPersonProbe.noteUpdatedByName,
        overrideHistory: hts.perfectPersonProbe.noteHistory,
        adminFollowUp: null,
        questionNumber: 0,
      }
    : null;

  const answersBySection = useMemo(() => {
    const bySection = new Map<string, AdminHtsAnswerDetail[]>();
    for (const answer of [...hts.answers].sort((a, b) => a.order - b.order)) {
      const list = bySection.get(answer.section) ?? [];
      list.push(answer);
      bySection.set(answer.section, list);
    }
    return [...bySection.entries()];
  }, [hts.answers]);

  const dimensionOverrideCount = Object.values(staging).filter((e) => e.kind === StagedTargetKind.Dimension && e.valueTouched).length;
  const answerOverrideCount = Object.values(staging).filter((e) => (e.kind === StagedTargetKind.Answer || e.kind === StagedTargetKind.HardGate) && e.valueTouched).length;
  const flagOverrideCount = Object.values(staging).filter((e) => e.kind === StagedTargetKind.ConsistencyFlag && e.valueTouched).length;
  const noteOnlyCount = Object.values(staging).filter((e) => !e.valueTouched && e.note.trim()).length;
  // Mirrors saveHtsAdminReviewCore's needsNarrativeRedraft — only a dimension override moves the composite/band/narrative.
  const changesCandidateView = dimensionOverrideCount > 0;

  useImperativeHandle(ref, () => ({
    openRelease: handleOpenConfirm,
    openSendFollowUps: () => setSendFollowUpsOpen(true),
  }));

  useEffect(() => {
    onFollowUpDraftCountChange?.(followUpDraftCount);
  }, [onFollowUpDraftCountChange, followUpDraftCount]);

  // Reported unconditionally, even with no readinessProfile — a submission with no scoreable pillar can still have staged notes, covered by canSaveRelease.
  useEffect(() => {
    onReleaseStatusChange?.({
      stagedCount,
      answerOverrideCount,
      dimensionOverrideCount,
      flagOverrideCount,
      noteOnlyCount,
      canRelease: canSaveRelease,
    });
  }, [
    onReleaseStatusChange,
    stagedCount,
    answerOverrideCount,
    dimensionOverrideCount,
    flagOverrideCount,
    noteOnlyCount,
    canSaveRelease,
  ]);

  return (
    <div className={styles.htsSection}>
      {readinessProfile && isPendingReview && (
        <Alert
          type="error"
          showIcon
          className={styles.htsAlert}
          message={isGuest ? 'Needs review' : 'Withheld from the candidate'}
          description={
            <>
              {isGuest
                ? `This is an access-code guest — they never see their results. A mandatory review trigger fired.${withheldReasons.length > 0 ? ' What triggered the review:' : ''}`
                : withheldReasons.length > 0
                  ? 'They see no score, band or summary until you release it. What triggered the review:'
                  : 'A mandatory review trigger fired. They see no score, band or summary until you release it.'}
              {withheldReasons.length > 0 && (
                <ul className={styles.reviewReasonList}>
                  {withheldReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </>
          }
        />
      )}

      {readinessProfile && (
        <Card
          title="Reviewer note"
          className={cn(styles.competencyCard, styles.adminNoteCard)}
          extra={
            <AntButton
              onClick={handleSaveAdminNote}
              loading={savingAdminNote}
              disabled={adminNoteDraft.trim() === (readinessProfile.adminNote ?? '')}
            >
              Save note
            </AntButton>
          }
        >
          <Typography.Text type="secondary">
            Never shown to the candidate. Saves immediately, independent of the staged changes below.
          </Typography.Text>
          <Input.TextArea
            className={styles.adminNoteInput}
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={adminNoteDraft}
            onChange={(e) => setAdminNoteDraft(e.target.value)}
          />
          {readinessProfile.adminNoteUpdatedAt && (
            <Typography.Text type="secondary" className={styles.scoringHint}>
              Last updated by {readinessProfile.adminNoteUpdatedByName ?? 'an admin'} on{' '}
              {new Date(readinessProfile.adminNoteUpdatedAt).toLocaleString()}
            </Typography.Text>
          )}
        </Card>
      )}

      <Card title="Summary" className={styles.headlineCard}>
        {!readinessProfile && <Typography.Text type="secondary">Not yet computed.</Typography.Text>}
        {readinessProfile && liveEffective && (
          <>
            <Statistic title="Composite Score" value={liveEffective.compositeScore} suffix="/ 100" />
            <Tag
              color={scoreToTagColor(liveEffective.compositeScore)}
              className={cn(styles.labelTag, stagedCount > 0 && styles.pendingBadge)}
            >
              {bandLabelFor(liveEffective.band)}
              {stagedCount > 0 && ' (pending)'}
            </Tag>

            {readinessProfile.detail?.narrative && (
              <Typography.Paragraph className={styles.narrative}>
                {readinessProfile.detail.narrative.coachSummary}
              </Typography.Paragraph>
            )}

            {readinessProfile.missingPillars.length > 0 && (
              <Typography.Text type="secondary">
                Not scored (no question tagged): {readinessProfile.missingPillars.map(pillarLabelFor).join(', ')}
              </Typography.Text>
            )}
          </>
        )}
      </Card>

      <Card
        title={`All submission answers (${hts.answers.length + (perfectPersonProbeAnswer ? 1 : 0)})`}
        className={styles.competencyCard}
      >
        {answersBySection.map(([section, answers]) => (
          <React.Fragment key={section}>
            <Typography.Title level={5} className={styles.sectionGroupTitle}>
              {section}
            </Typography.Title>
            {answers.map((answer) => (
              <AnswerRow
                key={answer.questionId}
                answer={answer}
                staging={staging}
                onStageAnswerClassification={(questionId, classification) =>
                  stageValue(questionId, StagedTargetKind.Answer, { classification })
                }
                onStageHardGate={(questionId, flagged, priority) => stageValue(questionId, StagedTargetKind.HardGate, { flagged, priority })}
                onStageNote={(questionId, fallbackKind, note) => stageNote(questionId, fallbackKind, note)}
                onDiscard={discardStagedEntry}
                credibilityNote={credibilityNoteByQuestionId.get(answer.questionId)}
                followUp={followUpControlsFor(answer.questionId)}
              />
            ))}
          </React.Fragment>
        ))}
        {perfectPersonProbeAnswer && (
          <AnswerRow
            key={perfectPersonProbeAnswer.questionId}
            answer={perfectPersonProbeAnswer}
            staging={staging}
            onStageAnswerClassification={() => {}}
            onStageHardGate={() => {}}
            onStageNote={(questionId, fallbackKind, note) => stageNote(questionId, fallbackKind, note)}
            onDiscard={discardStagedEntry}
            noteTargetKind={StagedTargetKind.Probe}
          />
        )}
      </Card>

      <Typography.Title level={3} className={styles.htsSectionTitle}>
        Pillar Scores
      </Typography.Title>

      {liveEffective?.pillars.map((pillar) => (
        <ReadinessPillarCard
          key={pillar.key}
          pillar={pillar}
          persistedPillar={persistedEffective?.pillars.find((p) => p.key === pillar.key)}
          computedAt={readinessProfile?.computedAt ?? ''}
          pillarWeightPercent={pillarWeightPercentByKey.get(pillar.key) ?? null}
          staging={staging}
          onStageScore={(pillarKey, dimensionKey, score) => stageValue(`${pillarKey}:${dimensionKey}`, StagedTargetKind.Dimension, { score })}
          onStageNote={(pillarKey, dimensionKey, note) => stageNote(`${pillarKey}:${dimensionKey}`, StagedTargetKind.Dimension, note)}
          onDiscardScore={(pillarKey, dimensionKey) => discardStagedEntry(`${pillarKey}:${dimensionKey}`)}
        />
      ))}

      <Card title={`Consistency flags (${hts.consistencyFlags.length})`} className={styles.competencyCard}>
        {hts.consistencyFlags.length === 0 && <Typography.Text type="secondary">None.</Typography.Text>}
        {hts.consistencyFlags.map((flag) => (
          <ConsistencyFlagCard
            key={flag.id}
            flag={flag}
            staging={staging}
            onStageDecision={(flagId, decision) => stageValue(flagId, StagedTargetKind.ConsistencyFlag, { decision })}
            onStageNote={(flagId, note) => stageNote(flagId, StagedTargetKind.ConsistencyFlag, note)}
            onDiscard={discardStagedEntry}
          />
        ))}
      </Card>

      {confirmOpen && reviewStep === 'changes' && (
        <Modal
          open
          title="Save these changes?"
          onCancel={() => setConfirmOpen(false)}
          footer={
            <div className={styles.modalFooterActions}>
              <Button appearance="outline" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button appearance="solid" loading={previewLoading} onClick={handlePreviewNarrative}>
                {changesCandidateView ? 'Generate new readiness profile' : 'Review readiness profile'}
              </Button>
            </div>
          }
        >
          <Typography.Paragraph>Are you sure you want to save these changes?</Typography.Paragraph>
          <div className={styles.confirmSummary}>
            <Typography.Text>
              {answerOverrideCount + flagOverrideCount} answer/flag override{answerOverrideCount + flagOverrideCount === 1 ? '' : 's'}
            </Typography.Text>
            <Typography.Text>
              {dimensionOverrideCount} dimension score override{dimensionOverrideCount === 1 ? '' : 's'}
            </Typography.Text>
            <Typography.Text>
              {noteOnlyCount} standalone note{noteOnlyCount === 1 ? '' : 's'} (no scoring effect)
            </Typography.Text>
            <div className={styles.confirmScoreRow}>
              <Typography.Text strong>Composite:</Typography.Text>
              <Typography.Text>{readinessProfile?.compositeScore ?? '—'}</Typography.Text>
              <Typography.Text>→</Typography.Text>
              <Typography.Text strong>{liveEffective?.compositeScore ?? '—'}</Typography.Text>
              {liveEffective && (
                <Tag color={scoreToTagColor(liveEffective.compositeScore)}>{bandLabelFor(liveEffective.band)}</Tag>
              )}
            </div>
          </div>
          {saveError && <Alert type="error" showIcon message={saveError} className={styles.provisionalAlert} />}
        </Modal>
      )}

      {confirmOpen && reviewStep === 'loading' && (
        <Modal open title="Review readiness profile" onCancel={() => setConfirmOpen(false)} footer={null}>
          {saveError ? (
            <>
              <Alert type="error" showIcon message={saveError} className={styles.provisionalAlert} />
              <Button appearance="solid" loading={previewLoading} onClick={handlePreviewNarrative}>
                Try again
              </Button>
            </>
          ) : (
            <div className={styles.previewLoading}>
              <Spin />
              <Typography.Text type="secondary">Loading readiness profile…</Typography.Text>
            </div>
          )}
        </Modal>
      )}

      {confirmOpen && reviewStep === 'narrative' && narrativeDraft && (
        <NarrativeReviewModal
          narrative={narrativeDraft}
          onChange={setNarrativeDraft}
          history={readinessProfile?.detail?.narrativeHistory ?? []}
          band={liveEffective?.band}
          strongPillarLabels={strongPillarLabels}
          focusPillarLabels={focusPillarLabels}
          regenerated={narrativePreview?.needsNarrativeRedraft ?? false}
          alreadyVisibleToCandidate={alreadyVisibleToCandidate}
          isGuest={isGuest}
          hasProfile={Boolean(readinessProfile)}
          scoringFailureUnresolved={readinessProfile?.scoringFailureUnresolved ?? false}
          scoringFailureAcknowledged={scoringFailureAcknowledged}
          onScoringFailureAcknowledgedChange={setScoringFailureAcknowledged}
          committing={committing}
          saveError={saveError}
          onBack={handleBackToChanges}
          onCancel={() => setConfirmOpen(false)}
          onRelease={handleConfirmSave}
        />
      )}

      <Modal
        open={sendFollowUpsOpen}
        destroyOnHidden
          // Cleared after closing so the content doesn't change mid-animation.
        afterClose={() => {
          if (hts.adminFollowUps) setFollowUpDrafts({});
        }}
        title={followUpDraftCount > 0 ? 'Send follow-up questions?' : 'No follow-up questions?'}
        width={followUpDraftCount > 0 ? 640 : 480}
        centered
        maskClosable={!sendingFollowUps}
        onCancel={() => !sendingFollowUps && setSendFollowUpsOpen(false)}
        footer={
          <div className={styles.modalFooterActions}>
            <Button appearance="outline" disabled={sendingFollowUps} onClick={() => setSendFollowUpsOpen(false)}>
              Cancel
            </Button>
            <Button appearance="solid" loading={sendingFollowUps} onClick={handleSendFollowUps}>
              {followUpDraftCount > 0 ? 'Send' : 'Confirm'}
            </Button>
          </div>
        }
      >
        {followUpDraftCount > 0 ? (
          <>
            <Typography.Paragraph type="secondary">
              The guest answers all of these in one go. You can send follow-up questions only once for this submission.
            </Typography.Paragraph>
            {[...hts.answers]
              .sort((a, b) => a.order - b.order)
              .filter((a) => followUpDrafts[a.questionId]?.trim())
              .map((a) => (
                <div key={a.questionId} className={styles.sendFollowUpItem}>
                  <Typography.Text type="secondary" className={styles.followUpDraftLabel}>
                    Question {a.questionNumber}
                  </Typography.Text>
                  <QAQuestion>{a.questionText}</QAQuestion>
                  {a.groupFields && a.groupFields.length > 0 ? (
                    <AnswerValue answer={a} />
                  ) : (
                    <Typography.Paragraph ellipsis={{ rows: 2, expandable: 'collapsible' }} className={styles.qaAnswer}>
                      {answerText(a.value)}
                    </Typography.Paragraph>
                  )}
                  <Typography.Text italic className={styles.adminFollowUpQuestion}>
                    {followUpDrafts[a.questionId].trim()}
                  </Typography.Text>
                </div>
              ))}
          </>
        ) : (
          <Typography.Paragraph>
            The candidate will be told their assessment is complete and won&apos;t be asked to come back. You can&apos;t send follow-up questions for this submission afterwards.
          </Typography.Paragraph>
        )}
      </Modal>
    </div>
  );
});

HtsSection.displayName = 'HtsSection';
